import React, { useCallback, useEffect, useRef, useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import {
  CloseIcon,
  IconSettingsSolid,
  IconUser,
} from "../components/CustomIcons";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import {
  addLeagueFollower,
  getUserDetails,
  getLeagueBySlug,
  hasUserFollowedLeague,
  updateLeagueTeams,
} from "../components/leagueLocker/firestoreQuery";
import { useAuth } from "../contexts/AuthContext";
import defaultBannerImage from "../assets/images/league-locker-banner.png";
import { Loader } from "lucide-react";
import { flRatingOptions } from "../constants/leagueLockerConfig";
import StyledLoadingSpinner from "../components/ui/StyledLoadingSpinner";
import { validateImageFile } from "../components/utils/imageValidation";
import { resizeImage } from "../components/utils/resizeImage";
import { cropImageFile } from "../components/utils/cropImage";
import Cropper from "react-easy-crop";
import { getDefaultPunishmentsImages, uploadImage } from "../services/firebase";
import { encryptObject } from "../components/utils/crypto";
import LeagueLockerAboutSection from "../components/leagueLocker/leagueLockerAboutSection";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { sendEmailOnFollowingLeague } from "../services/sendgrid-service";
import PostFeed from "../components/leagueLocker/postFeed";
import FeedBlock from "../components/leagueLocker/feedBlock";
import {
  collection,
  query,
  orderBy,
  getDocs,
  limit,
  startAfter,
  onSnapshot,
} from "firebase/firestore";
import { getFeedById } from "../components/leagueLocker/firestoreQuery";
import { db } from "../services/firebase";
import { useInfiniteQuery } from "@tanstack/react-query";
import { IUserDetails } from "../types/leagueLocker";
import SocialLinksRender from "../components/leagueLocker/socialLinksRender";
import LeagueLockerTeamsPage from "./LeagueLockerTeamsPage";
import LeagueLockerMerchSection from "./LeagueLockerMerchSection";
import LeagueLockerChallengeSection from "../components/leagueLocker/leagueLockerChallengeSection";
import JoinLeague from "../components/leagueLocker/joinLeague";

interface Iteam {
  name: string;
  isDuePaid: boolean;
  createdBy: string | undefined;
  joinedAt: string;
  imageUrl?: string;
  homeTown?: string;
  description?: string;
}
export type LeagueContext = { leagueUrl: string | null; isEncodedReq: false };

const TabGroup = Object.freeze({
  FEEDS: { id: 1, url: "feed" },
  CHALLENGES: { id: 2, url: "challenges" },
  TEAMS: { id: 3, url: "teams" },
  ABOUT: { id: 5, url: "about" },
  // RULES: { id: 4, url: "rules" },
  MERCH: { id: 6, url: "merch" },
});

const LeagueLockerPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isCommissioner, setIsCommissioner] = useState(false);
  const [openAddTeam, setOpenAddTeam] = useState(false);
  const [leagueData, setLeagueData] = useState<any>(null);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [inviteButtonText, setInviteButtonText] = useState<string | null>(
    "Invite"
  );
  const [team, setTeam] = useState<Iteam | null>(null);
  const [activeTab, setActiveTab] = useState(1);
  const [isAddingTeam, setIsAddingTeam] = useState(false);
  const navigate = useNavigate();
  const { tab, feedId } = useParams();
  const { leagueUrl, isEncodedReq } = useOutletContext<LeagueContext>();
  const [leagueDisplay, setLeagueDisplay] = useState<boolean>(false);
  const { user } = useAuth();

  /**
   * States for Image Upload & Cropper
   */
  const [teamImage, setTeamImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null); // existingImageUrl from leagueData if present
  const [showCropper, setShowCropper] = useState(false);
  const [tempImageUrl, setTempImageUrl] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedArea, setCroppedArea] = useState<any>(null);
  const [loadingCropper, setLoadingCropper] = useState(false);
  const teamImageRef = useRef<HTMLInputElement | null>(null);
  const [commissionerDetails, setCommissionerDetails] = useState<any | null>(
    null
  );
  const [defaultImageUrl, setDefaultImageUrl] = useState<String | null>(null);
  const [teamJoinError, setTeamJoinError] = useState<String | null>(null);
  const [isEditTeam, setIsEditTeam] = useState(false);
  const [isShowTeamDetails, setIsShowTeamDetails] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<Iteam | null>(null);
  const [selectedTeamNameOriginal, setSelectedTeamNameOriginal] = useState<
    string | null
  >(null);
  const [isFollower, setIsFollower] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [userDetails, setUserDetails] = useState<IUserDetails | null>(null);
  const [refreshLeague, setRefreshLeague] = useState<boolean>(false);

  useEffect(() => {
    if (!tab) {
      if (isEncodedReq) {
        setActiveTab(TabGroup.CHALLENGES.id);
      }
      if (!isEncodedReq) {
        navigate(`/${leagueUrl}/${TabGroup.CHALLENGES.url}`);
        setActiveTab(TabGroup.CHALLENGES.id);
      }
    }
    if (tab) {
      const tabId = getTabIdByUrl(tab);
      if (tabId > 0) {
        setActiveTab(tabId);
      } else {
        navigate(`/`);
      }
    }
  }, []);

  const getTabIdByUrl = (tab: string) => {
    switch (tab) {
      case "feed":
        return TabGroup.FEEDS.id;
      case "challenges":
        return TabGroup.CHALLENGES.id;
      case "teams":
        return TabGroup.TEAMS.id;
      // case "rules":
      //   return TabGroup.RULES.id;
      case "about":
        return TabGroup.ABOUT.id;
      case "merch":
        return TabGroup.MERCH.id;
      default:
        return 0;
    }
  };

  const fetchLeagueDetails = async () => {
    if (leagueUrl) {
      try {
        const res = await getLeagueBySlug(leagueUrl);
        console.log(res);
        if (res) {
          const commishRes = await getUserDetails(res?.createdBy);
          if (user) {
            const userRes = await getUserDetails(user?.uid);
            userRes && setUserDetails(userRes as IUserDetails);
          }
          setLeagueData(res);
          setCommissionerDetails(commishRes);
          const isFollowerRes = await hasUserFollowedLeague(
            user?.uid ?? "",
            res.id
          );
          setIsFollower(isFollowerRes);

          if (
            res.leaguePunishment?.cardImage === "" ||
            res.leaguePunishment?.cardImage === null
          ) {
            const imageData = await getDefaultPunishmentsImages();
            console.log(imageData?.[0]?.cardImageUrl);
            setDefaultImageUrl(imageData?.[0]?.cardImageUrl);
          }

          if (res.createdBy === user?.uid) {
            setIsCommissioner(true);
          }
        } else {
          navigate("/dashboard");
          setErrorText("No league found");
        }
      } catch (error) {
        setErrorText(error as string);
      }
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLeagueDetails();
  }, [leagueUrl, activeTab, user, refreshLeague]);
  useEffect(() => {
    if (leagueData) {
      let userId = user?.uid;
      let isUserIsCommissioner = leagueData.createdBy == userId ? true : false;
      let isUserIsMember = leagueData.teams
        ? leagueData.teams.some((user: any) => user.createdBy === userId)
        : false;

      /*
       * If Commissioner or League Member Always Show the League Page
       * If the user is not related to the League and the League is private ->
       * then only show the league page if he comes through the encoded joining link
       * If the League is Public then anyone can view the League Page
       * Private League always requires user to be Loggedin and it is the first condition
       */

      if (isUserIsCommissioner === true) {
        setLeagueDisplay(true);
      } else if (isUserIsMember === true) {
        setLeagueDisplay(true);
      } else {
        if (!leagueData.isPublic) {
          //Private
          if (isEncodedReq) {
            setLeagueDisplay(true);
          } else {
            setLeagueDisplay(false);
          }
        } else {
          setLeagueDisplay(true);
        }
      }
    }
  }, [leagueData]);

  // Create refs for each section
  const teamsRef = useRef<HTMLDivElement>(null);
  const aboutRef = useRef<HTMLDivElement>(null);
  const rulesRef = useRef<HTMLDivElement>(null);
  const feedRef = useRef<HTMLDivElement>(null);
  const merchRef = useRef<HTMLDivElement>(null);
  const [feeds, setFeeds] = useState<Array<any>>([]);
  const [rawFeeds, setRawFeeds] = useState<Array<any>>([]);
  const [singleFeed, setSingleFeed] = useState<any | null>(null);
  // sentinel ref removed; using callback ref below

  const PAGE_SIZE = 10;

  const fetchFeedsPage = async ({
    pageParam,
  }: {
    pageParam?: any;
  }): Promise<{ items: any[]; nextPageParam: any }> => {
    if (!leagueData?.id) return { items: [], nextPageParam: undefined };
    const feedsCol = collection(db, "leagueLockerFeed", leagueData.id, "feeds");
    let q;
    if (!pageParam) {
      q = query(feedsCol, orderBy("createdAt", "desc"), limit(PAGE_SIZE));
    } else {
      q = query(
        feedsCol,
        orderBy("createdAt", "desc"),
        startAfter(pageParam),
        limit(PAGE_SIZE)
      );
    }
    const snap = await getDocs(q);
    const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    const last = snap.docs[snap.docs.length - 1] || undefined;
    return { items, nextPageParam: last };
  };

  const infiniteQuery = useInfiniteQuery({
    queryKey: ["leagueFeeds", leagueData?.id],
    queryFn: async ({ pageParam = undefined }: { pageParam?: any }) =>
      fetchFeedsPage({ pageParam }),
    // disable infinite fetching when viewing a single feed via params
    enabled: !!leagueData?.id && activeTab === TabGroup.FEEDS.id && !feedId,
    getNextPageParam: (lastPage: { nextPageParam?: any }) =>
      lastPage.nextPageParam,
    initialPageParam: undefined,
  });

  // When query data changes, flatten pages into rawFeeds so enrichment effect can run
  useEffect(() => {
    // When viewing a single feed (feedId) we don't populate rawFeeds
    if (feedId) {
      setRawFeeds([]);
      return;
    }

    if (!infiniteQuery.data) {
      setRawFeeds([]);
      return;
    }
    const all = infiniteQuery.data.pages.flatMap((p: any) => p.items || []);
    setRawFeeds(all);
  }, [infiniteQuery.data, feedId]);

  // If feedId param is present, fetch only that feed once and skip realtime/infinite listeners
  useEffect(() => {
    let mounted = true;
    if (!leagueData?.id || !feedId || !leagueDisplay) {
      setSingleFeed(null);
      return;
    }

    (async () => {
      try {
        let doc = await getFeedById(leagueData.id, feedId);
        if (!mounted) return;
        if (doc) {
          const teams = leagueData?.teams || [];
          const author = teams.find((t: any) => t.createdBy === doc?.createdBy);
          doc = {
            ...doc,
            authorName: author ? author.name : "Unknown",
            authorPic: author ? author.imageUrl : null,
          };
          setSingleFeed(doc);
        } else {
          setSingleFeed(null);
        }
      } catch (err) {
        console.error("Error fetching single feed:", err);
        setSingleFeed(null);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [leagueData?.id, feedId, leagueData?.teams, leagueDisplay]);

  // Realtime listener: merge firestore updates (added/modified/removed) into rawFeeds
  useEffect(() => {
    // Skip realtime listener when viewing a single feed
    if (
      !leagueData?.id ||
      activeTab !== TabGroup.FEEDS.id ||
      feedId ||
      !leagueDisplay
    )
      return;

    // Number of items to listen for: at least page size or current loaded count
    const currentCount = infiniteQuery.data
      ? infiniteQuery.data.pages.flatMap((p: any) => p.items || []).length
      : PAGE_SIZE;

    const feedsCol = collection(db, "leagueLockerFeed", leagueData.id, "feeds");
    const realtimeQuery = query(
      feedsCol,
      orderBy("createdAt", "desc"),
      limit(Math.max(currentCount, PAGE_SIZE))
    );

    const unsubscribe = onSnapshot(realtimeQuery, (snapshot) => {
      if (!snapshot) return;

      setRawFeeds((prev) => {
        // Build map of existing items for O(1) updates
        const map = new Map<string, any>(
          prev.map((item: any) => [item.id, item])
        );

        snapshot.docChanges().forEach((change) => {
          const doc = { id: change.doc.id, ...change.doc.data() };
          if (change.type === "removed") {
            map.delete(doc.id);
          } else {
            // added or modified
            map.set(doc.id, doc);
          }
        });

        // Convert back to array and sort by createdAt desc
        const getTime = (item: any) => {
          const t = item?.createdAt;
          if (!t) return 0;
          // Firestore Timestamp has toMillis()
          if (typeof t === "object" && typeof t.toMillis === "function")
            return t.toMillis();
          if (typeof t === "number") return t;
          try {
            return new Date(t).getTime();
          } catch (e) {
            return 0;
          }
        };

        const merged = Array.from(map.values()).sort((a: any, b: any) => {
          return getTime(b) - getTime(a);
        });

        return merged;
      });
    });

    return () => unsubscribe();
  }, [leagueData?.id, activeTab, infiniteQuery.data, feedId, leagueDisplay]);

  // Callback ref for sentinel to observe with IntersectionObserver
  const sentinelObserverRef = useRef<IntersectionObserver | null>(null);
  const sentinelRefCallback = useCallback(
    (node: HTMLDivElement | null) => {
      if (sentinelObserverRef.current) {
        sentinelObserverRef.current.disconnect();
        sentinelObserverRef.current = null;
      }
      if (!node) return;
      sentinelObserverRef.current = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (
            entry.isIntersecting &&
            infiniteQuery.hasNextPage &&
            !infiniteQuery.isFetching
          ) {
            infiniteQuery.fetchNextPage();
          }
        });
      });
      sentinelObserverRef.current.observe(node);
    },
    [infiniteQuery] // Updated to include only infiniteQuery
  );

  // Enrich rawFeeds by matching createdBy to leagueData.teams
  useEffect(() => {
    if (!rawFeeds || rawFeeds.length === 0) {
      setFeeds([]);
      return;
    }

    const teams = leagueData?.teams || [];
    const enriched = rawFeeds
      .filter((f) => teams.some((t: any) => t.createdBy === f.createdBy))
      .map((f) => {
        const author = teams.find((t: any) => t.createdBy === f.createdBy)!;
        return {
          ...f,
          authorName: author.name,
          authorPic: author.imageUrl ?? null,
        };
      });

    setFeeds(enriched);
  }, [rawFeeds, leagueData?.teams]);

  // Removed unused scrollToSection function

  /**
   * Image Upload & Cropper Handlers
   */
  const handleImageChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const validationError = validateImageFile(file);
      if (validationError) {
        alert(`${validationError}`);
        return;
      }
      setLoadingCropper(true);
      try {
        const { resizedUrl } = await resizeImage(file);
        setTempImageUrl(resizedUrl);
        setShowCropper(true);
      } finally {
        setLoadingCropper(false);
      }
    },
    []
  );

  const onCropComplete = useCallback((_: any, croppedPixels: any) => {
    setCroppedArea(croppedPixels);
  }, []);

  const handleCropConfirm = useCallback(async () => {
    if (!tempImageUrl || !croppedArea) return;
    const cropped = await cropImageFile(tempImageUrl, croppedArea);
    if (cropped) {
      setTeamImage(cropped);
      setImagePreview(URL.createObjectURL(cropped));
    }
    setShowCropper(false);
  }, [tempImageUrl, croppedArea]);

  const handleRemoveImage = () => {
    setTeamImage(null);
    setImagePreview(null);
    if (teamImageRef.current) teamImageRef.current.value = "";
  };

  const handleEditTeam = async () => {
    setIsAddingTeam(true);
    try {
      const teamData = { ...selectedTeam };
      if (teamImage) {
        // Upload image to storage and get URL
        // Assuming uploadTeamImage is a function that uploads the image and returns the URL
        const imageUrl = await uploadImage(teamImage);
        teamData["imageUrl"] = imageUrl;
      }
      if (!imagePreview) {
        delete teamData["imageUrl"];
      }

      const updatedLeague = await updateLeagueTeams(
        leagueData.id,
        teamData,
        selectedTeamNameOriginal ?? "",
        false
      );
      if (!updatedLeague.success) {
        return setTeamJoinError(updatedLeague.message);
      }
      await fetchLeagueDetails(); // refresh state
      localStorage.removeItem("joinCode");
      setIsEditTeam(false);
      setIsShowTeamDetails(false);
      setSelectedTeam(null);
      setImagePreview(null);
      if (teamImageRef.current) teamImageRef.current.value = "";
    } catch (err) {
      console.error("Error joining league:", err);
      alert("Failed to join league. Please try again.");
    } finally {
      setIsAddingTeam(false);
    }
  };

  // const handlePunishmentClick = () => {
  //   if (leagueData?.leaguePunishment?.isCustomPunishment) {
  //     return;
  //   }
  //   const newTabUrl = `${window.location.origin}/punishments/${leagueData?.leaguePunishment?.slug}`;
  //   window.open(newTabUrl, "_blank");
  // };

  const handleOnFollow = async () => {
    try {
      setIsFollowing(true);
      if (!isFollower) {
        const res = await addLeagueFollower(leagueData.id, {
          uid: user?.uid ?? "",
          email: user?.email ?? "",
        });
        if (res) {
          await sendEmailOnFollowingLeague(
            commissionerDetails?.email,
            leagueData?.leagueName,
            commissionerDetails?.name?.split(" ")[0],
            userDetails?.name ?? "Someone"
          );
          setIsFollower(true);
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsFollowing(false);
    }
  };

  const onLeagueJoinSuccess = async () => {
    window.location.href = `/manage-league/${leagueUrl}/edit-my-team`;
  };

  return (
    <>
      {isLoading ? (
        <StyledLoadingSpinner />
      ) : (
        <main className="pageOuterWrapper">
          <Navbar />
          <div className="contentArea otherContentArea">
            <div className="wrapper">
              {leagueDisplay === true &&
                /**
                 * isEncodedReq true then show the join league screen
                 * else show the league page
                 */
                (isEncodedReq ? (
                  <JoinLeague
                    leagueData={leagueData}
                    onJoinSuccess={() => onLeagueJoinSuccess()}
                  />
                ) : (
                  <>
                    <div className="innerBanner">
                      <img
                        src={leagueData?.bannerImage || defaultBannerImage}
                        alt="League Banner"
                      />
                      {isCommissioner && (
                        <span
                          className="actionItem"
                          onClick={() =>
                            navigate(`/manage-league/${leagueUrl}`)
                          }
                        >
                          <IconSettingsSolid />
                        </span>
                      )}
                      <div className="innerBannerContent">
                        <h1 className="bannerTitle">
                          <span>{leagueData?.leagueName}</span>
                        </h1>
                        {/* <p className="followTxt">1.5M Followers</p> */}
                        <Markdown remarkPlugins={[remarkGfm]}>
                          {leagueData?.leagueBio ?? ""}
                        </Markdown>
                        <div className="bannerBottomCont">
                          <div className="leftBtnWrap">
                            {!isCommissioner && user && (
                              <button
                                className="btnStyle btnFilled"
                                onClick={handleOnFollow}
                                disabled={isFollowing || isFollower}
                              >
                                {isFollower ? "FOLLOWING" : "FOLLOW +"}
                              </button>
                            )}
                            {/* <button className="btnStyle btnTransparent">
                              SHARE <IconShare2 />
                            </button> */}
                          </div>
                          <SocialLinksRender leagueData={leagueData} />
                        </div>
                      </div>
                    </div>

                    <div className="tabContentWrap">
                      <ul className="tabMenu">
                        <li
                          className={
                            activeTab === TabGroup.FEEDS.id ? "active" : ""
                          }
                          onClick={() => {
                            if (!isEncodedReq) {
                              navigate(`/${leagueUrl}/${TabGroup.FEEDS.url}`);
                            }
                            setActiveTab(TabGroup.FEEDS.id);
                          }}
                        >
                          Feed
                        </li>

                        <li
                          className={
                            activeTab === TabGroup.CHALLENGES.id ? "active" : ""
                          }
                          onClick={() => {
                            if (!isEncodedReq) {
                              navigate(
                                `/${leagueUrl}/${TabGroup.CHALLENGES.url}`
                              );
                            }
                            setActiveTab(TabGroup.CHALLENGES.id);
                          }}
                        >
                          Challenges
                        </li>
                        <li
                          className={
                            activeTab === TabGroup.TEAMS.id ? "active" : ""
                          }
                          onClick={() => {
                            if (!isEncodedReq) {
                              navigate(`/${leagueUrl}/${TabGroup.TEAMS.url}`);
                            }
                            setActiveTab(TabGroup.TEAMS.id);
                          }}
                        >
                          Teams
                        </li>

                        <li
                          className={
                            activeTab === TabGroup.ABOUT.id ? "active" : ""
                          }
                          onClick={() => {
                            if (!isEncodedReq) {
                              navigate(`/${leagueUrl}/${TabGroup.ABOUT.url}`);
                            }
                            setActiveTab(TabGroup.ABOUT.id);
                          }}
                        >
                          About
                        </li>
                        <li
                          className={
                            activeTab === TabGroup.MERCH.id ? "active" : ""
                          }
                          onClick={() => {
                            if (!isEncodedReq) {
                              navigate(`/${leagueUrl}/${TabGroup.MERCH.url}`);
                            }
                            setActiveTab(TabGroup.MERCH.id);
                          }}
                        >
                          Merch
                        </li>
                      </ul>
                      <div className="tabContent">
                        {activeTab === TabGroup.FEEDS.id && (
                          <div
                            ref={feedRef}
                            style={{ scrollMarginTop: "100px" }}
                          >
                            {leagueData.teams?.find(
                              (team: Iteam) => team.createdBy === user?.uid
                            ) &&
                              !feedId && (
                                <PostFeed
                                  league={leagueData}
                                  onPosted={() => {
                                    try {
                                      infiniteQuery.refetch();
                                    } catch (e) {}
                                  }}
                                />
                              )}

                            {/* If a single feed is requested via params, show it with a back button */}
                            {feedId ? (
                              <>
                                {singleFeed ? (
                                  <FeedBlock
                                    key={singleFeed.id}
                                    feed={singleFeed}
                                    leagueId={leagueData.id}
                                    commissionerDetails={commissionerDetails}
                                    isSharedFeed={true}
                                    leagueUrl={leagueData.leagueUrl}
                                    setActiveTab={setActiveTab}
                                  />
                                ) : (
                                  <div className="feedBlk">
                                    <div className="feedContent">
                                      <p>Feed is not available</p>
                                    </div>
                                  </div>
                                )}
                                <div className="btnWrap">
                                  <button
                                    className="btnFilled"
                                    onClick={() => {
                                      if (!isEncodedReq) {
                                        navigate(
                                          `/${leagueUrl}/${TabGroup.FEEDS.url}`
                                        );
                                      } else {
                                        navigate(
                                          `/${leagueUrl}/${TabGroup.FEEDS.url}`
                                        );
                                      }
                                      setSingleFeed(null);
                                      try {
                                        infiniteQuery.refetch();
                                      } catch (e) {}
                                    }}
                                  >
                                    BACK TO FEED
                                  </button>
                                </div>
                              </>
                            ) : (
                              <>
                                {/* Render realtime feeds */}
                                {feeds.length === 0 ? (
                                  <></>
                                ) : (
                                  feeds.map((f) => (
                                    <FeedBlock
                                      key={f.id}
                                      feed={f}
                                      setActiveTab={setActiveTab}
                                      leagueId={leagueData.id}
                                      commissionerDetails={commissionerDetails}
                                      leagueUrl={leagueData.leagueUrl}
                                      createdByName={
                                        leagueData.teams?.find(
                                          (team: Iteam) =>
                                            team.createdBy === f.createdBy
                                        )?.name
                                      }
                                      createdByImage={
                                        leagueData.teams?.find(
                                          (team: Iteam) =>
                                            team.createdBy === f.createdBy
                                        )?.imageUrl
                                      }
                                    />
                                  ))
                                )}
                                <div ref={sentinelRefCallback} />
                                {infiniteQuery.isFetchingNextPage && (
                                  <p>Loading more...</p>
                                )}
                              </>
                            )}
                          </div>
                        )}

                        {activeTab === TabGroup.ABOUT.id && (
                          <div
                            ref={aboutRef}
                            style={{ scrollMarginTop: "100px" }}
                          >
                            <LeagueLockerAboutSection />
                          </div>
                        )}

                        {activeTab === TabGroup.CHALLENGES.id && (
                          <>
                            <LeagueLockerChallengeSection
                              challenges={
                                leagueData.challenges
                                  ? leagueData.challenges
                                  : []
                              }
                              leagueUrl={leagueData.leagueUrl}
                              leagueData={leagueData}
                              allTeams={
                                leagueData.teams ? leagueData.teams : []
                              }
                              challengeId={feedId}
                              setRefreshLeague={setRefreshLeague}
                              refreshLeague={refreshLeague}
                            />
                          </>
                        )}

                        {activeTab === TabGroup.TEAMS.id && (
                          <LeagueLockerTeamsPage />
                        )}

                        {activeTab === TabGroup.MERCH.id && (
                          <div
                            ref={merchRef}
                            style={{ scrollMarginTop: "100px" }}
                          >
                            <LeagueLockerMerchSection />
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                ))}

              {leagueDisplay === false && (
                <>
                  <div className="sectionBlk privateBlk">
                    <div className="sectionHeading1">Information:</div>
                    <p>This League has been set to Private.</p>
                  </div>
                </>
              )}
            </div>
          </div>
          <Footer />
        </main>
      )}
      {openAddTeam && (
        <div className="popupWrapper">
          <div className="modalPaymentHandle">
            <div className="popupHeading">Enter Team Name</div>
            <p>Edit my team</p>
          </div>
        </div>
      )}
      {isShowTeamDetails && (
        <div className="popupWrapper">
          <div className="modalPaymentHandle">
            <div
              className="closeModal"
              onClick={() => {
                setIsShowTeamDetails(false);
                setIsEditTeam(false);
                setSelectedTeam(null);
                setTeamJoinError(null);
              }}
            >
              <CloseIcon />
            </div>
            {isEditTeam ? (
              <>
                {/* <div className="memberPic"><img src={selectedTeam?.imageUrl} alt="" /></div> */}
                {showCropper && tempImageUrl && (
                  <div className="popupWrapper fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center">
                    <div className="relative w-[90vw] max-w-2xl h-[90vh] bg-white rounded-lg overflow-hidden shadow-lg">
                      <Cropper
                        image={tempImageUrl}
                        crop={crop}
                        zoom={zoom}
                        aspect={4 / 4}
                        onCropChange={setCrop}
                        onZoomChange={setZoom}
                        onCropComplete={onCropComplete}
                      />
                      <div className="absolute bottom-0 left-0 w-full flex justify-between p-4 bg-white border-t">
                        <button
                          type="button"
                          onClick={() => setShowCropper(false)}
                          className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={handleCropConfirm}
                          className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                        >
                          Crop & Save
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                <div className="uploadArea">
                  {!imagePreview && (
                    <>
                      <input
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        ref={teamImageRef}
                        onChange={handleImageChange}
                      />
                      <button
                        className="btnStyle btnHighlighted"
                        type="button"
                        onClick={() => teamImageRef.current?.click()}
                      >
                        UPLOAD TEAM IMAGE
                      </button>
                    </>
                  )}

                  {imagePreview && (
                    <div className="relative inline-block">
                      <img
                        src={imagePreview}
                        alt="Punishment Preview"
                        style={{ maxWidth: 150, borderRadius: 8 }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          handleRemoveImage();
                        }}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600"
                        style={{ lineHeight: 1, fontSize: 16 }}
                        aria-label="Remove image"
                      >
                        ×
                      </button>
                    </div>
                  )}
                </div>
                <div className="formGroup">
                  <input
                    type="text"
                    className="fldStyle"
                    placeholder="Enter your team name"
                    maxLength={30}
                    value={selectedTeam?.name ?? ""}
                    onChange={(e) => {
                      const name = e.target.value;
                      // Prevents the very first character from being a space
                      if (selectedTeam?.name.length === 0 && name === " ") {
                        return;
                      }
                      setSelectedTeam((prev) =>
                        prev ? { ...prev, name: name } : null
                      );
                      setTeamJoinError(null);
                    }}
                    // Add the onBlur event handler here
                    onBlur={() => {
                      if (selectedTeam?.name) {
                        const trimmedName = selectedTeam.name.trim();
                        setSelectedTeam((prev) =>
                          prev ? { ...prev, name: trimmedName } : null
                        );
                      }
                    }}
                  />
                  {teamJoinError && (
                    <div className="errorText">{teamJoinError}</div>
                  )}
                </div>
                <div className="formGroup">
                  <textarea
                    placeholder="Enter the details of your life story here, something short and sweet"
                    className="textareaStyle"
                    maxLength={250}
                    value={selectedTeam?.description ?? ""}
                    onChange={(e) =>
                      setSelectedTeam((prev) =>
                        prev ? { ...prev, description: e.target.value } : null
                      )
                    }
                  ></textarea>
                </div>
                <div className="formGroup">
                  <input
                    type="text"
                    className="fldStyle"
                    maxLength={40}
                    placeholder="Enter your hometown"
                    value={selectedTeam?.homeTown ?? ""}
                    onChange={(e) =>
                      setSelectedTeam((prev) =>
                        prev ? { ...prev, homeTown: e.target.value } : null
                      )
                    }
                  />
                </div>
                <div className="btnWrap">
                  <button
                    className="btnStyle btnHighlighted"
                    onClick={handleEditTeam}
                    disabled={
                      isAddingTeam ||
                      !selectedTeam?.name ||
                      selectedTeam.name.trim().length === 0
                    }
                  >
                    SAVE
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="teamMemberDetail">
                  <div className="memberPic">
                    {selectedTeam?.imageUrl ? (
                      <img src={selectedTeam.imageUrl} alt="" />
                    ) : (
                      <IconUser width={107} />
                    )}
                  </div>
                  <div className="userName">
                    <div className="popupHeading">{selectedTeam?.name}</div>
                    {selectedTeam?.homeTown && (
                      <span>{selectedTeam?.homeTown}</span>
                    )}
                  </div>
                </div>
                {selectedTeam?.description && (
                  <p>{selectedTeam?.description}</p>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default LeagueLockerPage;
